import { useCallback } from "react"
import { type ConnectDropTarget, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"

interface UseMultiFileDropProps {
  onDrop: (files: File[]) => void
  enabled?: boolean
}

interface MultiFileDropHook {
  dropRef: ConnectDropTarget
  isOver: boolean
  canDrop: boolean
}

export const useMultiFileDrop = ({ onDrop, enabled = true }: UseMultiFileDropProps): MultiFileDropHook => {
  const handleDrop = useCallback(
    (item: { files: File[] }) => {
      if (!enabled) return false

      const files = item.files
      if (!files?.length) return false

      onDrop(files)
      return true
    },
    [onDrop, enabled]
  )

  const [{ isOver, canDrop }, dropRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: (item: { files: File[] }) => handleDrop(item),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop() && enabled,
      }),
      canDrop: (item: { files: File[] }) => {
        return enabled && Boolean(item.files?.length)
      },
    }),
    [handleDrop, enabled]
  )

  return {
    dropRef,
    isOver,
    canDrop,
  }
}
